import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getOrganization,
  updateOrganization,
  getOrgProfiles,
  createOrgProfile,
  deleteProfile,
  uploadOrgLogo,
} from "../services/api";
import { useTranslation } from "../i18n/LanguageContext";
import {
  Building2,
  Plus,
  Settings,
  Trash2,
  ArrowLeft,
  Upload,
  X,
  FileText,
} from "lucide-react";

export default function OrganizationDetailPage() {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [org, setOrg] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create profile modal
  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ profile_name: "", job_title: "" });
  const [creatingProfile, setCreatingProfile] = useState(false);

  // Edit org
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadData();
  }, [orgId]);

  const loadData = async () => {
    try {
      const [orgRes, profilesRes] = await Promise.all([
        getOrganization(orgId),
        getOrgProfiles(orgId),
      ]);
      setOrg(orgRes.data.organization);
      setProfiles(profilesRes.data.profiles);
      setEditForm({
        name: orgRes.data.organization.name,
        description: orgRes.data.organization.description || "",
      });
    } catch {
      navigate("/app/organizations");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrg = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await updateOrganization(orgId, editForm);
      setOrg(res.data.organization);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await uploadOrgLogo(orgId, formData);
      setOrg(res.data.organization);
    } catch {
      setError("Failed to upload logo");
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    if (!profileForm.profile_name.trim()) return;
    setCreatingProfile(true);
    setError("");
    try {
      const res = await createOrgProfile(orgId, profileForm);
      setProfiles((prev) => [...prev, res.data.profile]);
      setShowCreateProfile(false);
      setProfileForm({ profile_name: "", job_title: "" });
    } catch (err) {
      setError(err.response?.data?.error || t("orgs.createProfileFailed"));
    } finally {
      setCreatingProfile(false);
    }
  };

  const handleDeleteProfile = async (profileSlug) => {
    try {
      await deleteProfile(profileSlug);
      setProfiles((prev) => prev.filter((p) => p.slug !== profileSlug));
      setDeleteConfirm(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete");
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!org) return null;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back */}
      <button
        onClick={() => navigate("/app/organizations")}
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4 transition"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("orgs.backToOrgs")}
      </button>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Org header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <label className="relative cursor-pointer group">
              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              {org.logo_image ? (
                <div className="relative">
                  <img src={org.logo_image} alt="" className="w-14 h-14 rounded-xl object-cover" />
                  <div className="absolute inset-0 bg-black/30 rounded-xl opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                    <Upload className="w-4 h-4 text-white" />
                  </div>
                </div>
              ) : (
                <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition">
                  <Building2 className="w-7 h-7 text-blue-600" />
                </div>
              )}
            </label>
            <div>
              {editing ? (
                <form onSubmit={handleSaveOrg} className="space-y-2">
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    value={editForm.description}
                    onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                    className="block w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t("orgs.description")}
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {t("orgs.save")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(false)}
                      className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700"
                    >
                      {t("orgs.cancel")}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <h1 className="text-xl font-bold text-gray-900">{org.name}</h1>
                  {org.description && (
                    <p className="text-sm text-gray-500 mt-0.5">{org.description}</p>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition"
                title={t("orgs.editOrg")}
              >
                <Settings className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => navigate(`/app/org/${orgId}/template`)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50 transition"
            >
              <FileText className="w-4 h-4" />
              {t("orgs.template")}
            </button>
          </div>
        </div>
      </div>

      {/* Profiles section */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{t("orgs.orgProfiles")}</h2>
        <button
          onClick={() => setShowCreateProfile(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="w-4 h-4" />
          {t("orgs.createProfile")}
        </button>
      </div>

      {/* Create profile modal */}
      {showCreateProfile && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t("orgs.createProfile")}</h2>
              <button onClick={() => setShowCreateProfile(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">{t("orgs.createProfileDesc")}</p>
            <form onSubmit={handleCreateProfile} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("orgs.profileName")}</label>
                <input
                  type="text"
                  value={profileForm.profile_name}
                  onChange={(e) => setProfileForm((f) => ({ ...f, profile_name: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("orgs.jobTitle")}</label>
                <input
                  type="text"
                  value={profileForm.job_title}
                  onChange={(e) => setProfileForm((f) => ({ ...f, job_title: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateProfile(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition"
                >
                  {t("orgs.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={creatingProfile || !profileForm.profile_name.trim()}
                  className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  {creatingProfile ? t("orgs.creating") : t("orgs.createProfile")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete profile confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t("orgs.deleteProfileTitle")}</h3>
            <p className="text-sm text-gray-500 mb-5">{t("orgs.deleteProfileDesc")}</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition">
                {t("orgs.cancel")}
              </button>
              <button
                onClick={() => handleDeleteProfile(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition"
              >
                {t("orgs.delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile list */}
      {profiles.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <p className="text-gray-500 text-sm">{t("orgs.noProfiles")}</p>
          <p className="text-gray-400 text-xs mt-1">{t("orgs.noProfilesDesc")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {profiles.map((profile) => (
            <div
              key={profile.id}
              className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition cursor-pointer overflow-hidden"
              onClick={() => navigate(`/app/profile/${profile.slug}`)}
            >
              {profile.cover_image && (
                <div className="h-20 overflow-hidden">
                  <img src={profile.cover_image} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {profile.profile_image ? (
                      <img src={profile.profile_image} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                        style={{ backgroundColor: profile.theme_color || "#3B82F6" }}
                      >
                        {profile.profile_name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h3 className="font-medium text-gray-900 text-sm">{profile.profile_name}</h3>
                      {profile.job_title && (
                        <p className="text-xs text-gray-500 line-clamp-1">{profile.job_title}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteConfirm(profile.slug);
                    }}
                    className="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-red-50 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
